import type { ShortTaskOutput } from "@orsane/schema/tasks";
import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import * as React from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { useTRPC } from "@/lib/trpc";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface TaskSelectProps {
    value: ShortTaskOutput | null;
    ignoreId: number | undefined;
    onChange: (value: ShortTaskOutput | null) => void;
    trigger: (content: React.ReactNode, value: ShortTaskOutput | null) => React.ReactElement;
}

export function TaskSelect({ value, ignoreId, onChange, trigger }: TaskSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [debouncedSearch, setDebouncedSearch] = React.useState("");

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const trpc = useTRPC();
    const { data } = useQuery(
        trpc.task.search.queryOptions(
            { search: debouncedSearch || undefined, ignoreId: ignoreId, limit: 20 },
            { enabled: open, staleTime: 0 }
        )
    );

    const handleSelect = (v: string) => {
        if (v !== "none") {
            const selectedTask = data?.find((t) => t.id === Number(v));
            if (!selectedTask) {
                throw new Error("Selected task not found in list of tasks");
            }

            if (value?.id === selectedTask.id) {
                onChange(null);
            } else {
                onChange(selectedTask);
                setOpen(false);
            }
        } else {
            onChange(null);
            setOpen(false);
        }
    };

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) setSearch("");
            }}
        >
            <PopoverTrigger
                render={trigger(
                    value ? (
                        <div className="flex items-center gap-1.5">
                            <span className="text-foreground-muted">T-{value.number}</span>
                            <span>{value.title}</span>
                        </div>
                    ) : null,
                    value
                )}
            />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="start" className="w-full p-0 max-w-80 gap-0!">
                <Command shouldFilter={false}>
                    <CommandInput
                        autoFocus
                        placeholder="Search for a task..."
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandGroup>
                            <CommandItem value={"none"} onClick={(e) => e.stopPropagation()} onSelect={handleSelect}>
                                None
                            </CommandItem>
                            {data?.map((task) => (
                                <CommandItem
                                    value={task.id.toString()}
                                    key={task.id}
                                    onClick={(e) => e.stopPropagation()}
                                    onSelect={handleSelect}
                                >
                                    <span className="text-foreground-muted shrink-0">T-{task.number}</span>
                                    <span className="truncate">{task.title}</span>
                                    <Check
                                        className={`size-3.5 text-foreground shrink-0 ml-auto ${value?.id === task.id ? "opacity-100" : "opacity-0"}`}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        <CommandEmpty>No tasks found.</CommandEmpty>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
